/**
 * Participants Tab — Stage 0 Configuration UI
 *
 * CRUD table for managing participants, with inline editing,
 * group filtering, and blackout period management.
 */

import type { CertificationDefinition, Participant, PreflightResult } from '../models/types';
import { fmtTime } from '../utils/date-utils';
import * as store from './config-store';
import { openParticipantSetFormatSheet, openXlsxImportFlow } from './data-transfer-ui';
import { triggerCharacterEffect, triggerCharacterFarewell } from './easter-eggs';
import { renderPakalBadges } from './pakal-utils';
import { showParticipantEditor } from './participant-editor-sheet';
import {
  canLeaveTableEdit,
  enterTableEditMode,
  exitTableEditMode,
  isTableEditActive,
  renderTableEditMode,
  wireTableEditEvents,
} from './table-edit-participants';
import { certBadges, escAttr, escHtml, formatMetadataDate, groupBadge, levelBadge, SVG_ICONS } from './ui-helpers';
import { showConfirm, showToast } from './ui-modal';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DEFAULT_WORKLOAD_MULTIPLIER = 1;

/** Compact display for a workload multiplier (e.g. 0.5, 1.25, 2). */
export function formatWorkloadMultiplier(m: number): string {
  return Number(m.toFixed(2)).toString();
}

/** Badge shown in the participant row when workloadMultiplier ≠ 1.
 *  Returns '' when the multiplier is the default. */
function workloadMultBadge(mult: number | undefined): string {
  if (mult === undefined || Math.abs(mult - 1) < 1e-9) return '';
  const display = formatWorkloadMultiplier(mult);
  return ` <span class="badge badge-sm workload-mult-badge"
       title="מקדם עומס ${display} — יעד ההוגנות מותאם בהתאם.">מקדם ×${display}</span>`;
}

function getCertOptions(): CertificationDefinition[] {
  return store.getCertificationDefinitions();
}

/** Check if a participant holds any deleted certification or pakal. */
function hasOrphanedRefs(p: Participant): boolean {
  const activeCertIds = new Set(getCertOptions().map((d) => d.id));
  if (p.certifications.some((c) => !activeCertIds.has(c))) return true;
  const activePakalIds = new Set(store.getPakalDefinitions().map((d) => d.id));
  if ((p.pakalIds || []).some((id) => !activePakalIds.has(id))) return true;
  return false;
}

/** Format the day-range prefix of a DateUnavailability rule as "יום N" or "יום N–יום M". */
function formatRuleDayRangeShort(r: { dayIndex: number; endDayIndex?: number }): string {
  const start = r.dayIndex;
  const end = r.endDayIndex ?? r.dayIndex;
  return start === end ? `יום ${start}` : `יום ${start}–${end}`;
}
function formatRuleDayRangeLong(r: { dayIndex: number; endDayIndex?: number }): string {
  const start = r.dayIndex;
  const end = r.endDayIndex ?? r.dayIndex;
  return start === end ? `יום ${start}` : `יום ${start}–יום ${end}`;
}

/** Compact unavailability chips rendered below participant name. */
function renderUnavailChips(pid: string): string {
  const rules = store.getDateUnavailabilities(pid);
  if (rules.length === 0) return '';

  const MAX_VISIBLE = 3;
  const visible = rules.slice(0, MAX_VISIBLE);

  const chips = visible
    .map((r) => {
      const day = formatRuleDayRangeShort(r);
      const time = r.allDay
        ? 'כל היום'
        : `${String(r.startHour).padStart(2, '0')}-${String(r.endHour).padStart(2, '0')}`;
      const tooltipTime = r.allDay
        ? 'כל היום'
        : `${String(r.startHour).padStart(2, '0')}:00–${String(r.endHour).padStart(2, '0')}:00`;
      const tooltip = `${formatRuleDayRangeLong(r)} ${tooltipTime}${r.reason ? ` (${r.reason})` : ''}`;
      return `<span class="unavail-chip" title="${escHtml(tooltip)}">${day} ${time}</span>`;
    })
    .join('');

  const overflow =
    rules.length > MAX_VISIBLE
      ? `<span class="unavail-chip unavail-chip-more" title="${rules.length} כללי אי-זמינות">+${rules.length - MAX_VISIBLE}</span>`
      : '';

  return `<div class="unavail-chips" data-action="toggle-blackouts" data-pid="${pid}">${chips}${overflow}</div>`;
}

function renderPreferenceBadges(p: Participant): string {
  const parts: string[] = [];
  if (p.preferredTaskName) {
    parts.push(`<span class="badge badge-sm" style="background:#27ae60">מעדיף: ${escHtml(p.preferredTaskName)}</span>`);
  }
  if (p.lessPreferredTaskName) {
    parts.push(`<span class="badge badge-sm" style="background:#e67e22">פחות מועדף: ${escHtml(p.lessPreferredTaskName)}</span>`);
  }
  return parts.length > 0 ? parts.join(' ') : '<span class="text-muted">—</span>';
}

// ─── State ───────────────────────────────────────────────────────────────────

let filterGroup: string = '';
let sortColumn: 'name' | 'group' | 'level' | '' = '';
let sortDirection: 'asc' | 'desc' = 'asc';
/** Live name-search query. Applied as a DOM-overlay filter (see applySearchFilter)
 *  rather than through getVisibleParticipants, so typing never triggers a full
 *  rerender — the search input keeps focus and the mobile keyboard stays open. */
let filterText: string = '';

/** Case-insensitive name match against the active search query. */
function matchesSearch(p: Participant): boolean {
  const q = filterText.trim().toLowerCase();
  return q === '' || p.name.toLowerCase().includes(q);
}

// ─── Multi-Select State ──────────────────────────────────────────────────────

const selectedIds: Set<string> = new Set();
/** Tracks the last-clicked participant ID for Shift+Click range selection */
let _lastClickedId: string | null = null;
/** When true the bulk unavailability dialog is open */
let _bulkDialogOpen = false;
/** When true the bulk delete confirmation dialog is open */
let _bulkDeleteDialogOpen = false;
/** Participant IDs whose detail row is expanded. Promoted to module state so the
 *  expansion survives a rerender — the row builder re-emits `row-expanded` from
 *  this set, and selection changes patch the DOM surgically instead of rerendering. */
const expandedIds: Set<string> = new Set();

function getVisibleParticipants(allParticipants: Participant[] = store.getAllParticipants()): Participant[] {
  const filtered = filterGroup ? allParticipants.filter((p) => p.group === filterGroup) : allParticipants;
  return sortParticipants(filtered);
}

function reconcileSelection(visibleParticipants: Participant[] = getVisibleParticipants()): void {
  const visibleIds = new Set(visibleParticipants.map((p) => p.id));

  for (const id of Array.from(selectedIds)) {
    if (!visibleIds.has(id)) selectedIds.delete(id);
  }

  if (_lastClickedId && !visibleIds.has(_lastClickedId)) {
    _lastClickedId = null;
  }

  if (selectedIds.size === 0) {
    _bulkDialogOpen = false;
    _bulkDeleteDialogOpen = false;
  }
}

/** Drop expanded IDs for participants that no longer exist (e.g. after a delete).
 *  Expansion is intentionally kept across group-filter changes. */
function reconcileExpanded(): void {
  if (expandedIds.size === 0) return;
  const existing = new Set(store.getAllParticipants().map((p) => p.id));
  for (const id of Array.from(expandedIds)) {
    if (!existing.has(id)) expandedIds.delete(id);
  }
}

// ─── Participant Sets Panel State ────────────────────────────────────────────

let _setsPanelOpen = false;
let _setsFormMode: 'none' | 'save-as' | 'rename' = 'none';
let _setsFormError = '';
let _setsRenameTargetId: string | null = null;

/** Clear participant selection state (called on tab change) */
export function clearParticipantSelection(): void {
  if (isTableEditActive()) exitTableEditMode();
  selectedIds.clear();
  expandedIds.clear();
  _lastClickedId = null;
  _bulkDialogOpen = false;
  _bulkDeleteDialogOpen = false;
  syncBulkSelectionMode();
}

/** Full reset of module-level view state. Called by the tutorial demo mode
 *  on tour entry and exit so a filter/sort the user set during one tour
 *  doesn't leak into the next, and the demo's full 6-participant roster
 *  isn't accidentally filtered to one group. */
export function resetParticipantsTabViewState(): void {
  filterGroup = '';
  filterText = '';
  sortColumn = '';
  sortDirection = 'asc';
  _setsPanelOpen = false;
  _setsFormMode = 'none';
  _setsFormError = '';
  _setsRenameTargetId = null;
  clearParticipantSelection();
}

/**
 * Async guard for tab switching: if table-edit mode has unsaved changes,
 * show a save/discard/continue dialog. Returns true if leaving is allowed.
 */
export async function canLeaveParticipantsTab(): Promise<boolean> {
  return canLeaveTableEdit();
}
// ─── Sort Logic ──────────────────────────────────────────────────────────────

function sortParticipants(list: Participant[]): Participant[] {
  if (!sortColumn) {
    // Default "natural roster" order when no column is explicitly selected:
    // group A→Z, then rank senior-first (L4→L0), then name. This slots a
    // newly added participant into its correct position instead of appending
    // it at the bottom (storage/insertion order). Explicit column clicks
    // override this with the single-key sorts below. The direction toggle
    // flips the whole composite order (group Z→A, junior-first, name Z→A).
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...list].sort(
      (a, b) =>
        dir * (a.group.localeCompare(b.group, 'he') || b.level - a.level || a.name.localeCompare(b.name, 'he')),
    );
  }
  const dir = sortDirection === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (sortColumn) {
      case 'name':
        return dir * a.name.localeCompare(b.name);
      case 'group':
        return dir * a.group.localeCompare(b.group) || a.name.localeCompare(b.name);
      case 'level':
        return dir * (a.level - b.level) || a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });
}

function sortIndicator(col: string): string {
  if (sortColumn !== col) return '';
  return sortDirection === 'asc' ? ' ▲' : ' ▼';
}

// ─── Participant Sets Panel ───────────────────────────────────────────────────

function renderSetsPanel(): string {
  const sets = store.getAllParticipantSets();
  const activeId = store.getActiveParticipantSetId();
  const dirty = store.isParticipantSetDirty();

  let html = `<div class="preset-panel pset-panel">`;

  // Header
  html += `<div class="preset-panel-header">
    <h3>📋 סטים של משתתפים <span class="count">${sets.length}</span></h3>
    <div class="preset-header-actions">
      <button class="btn-xs btn-outline" data-action="pset-import-xlsx" title="ייבוא סט מקובץ Excel">📊 ייבוא Excel</button>
      <button class="btn-xs btn-outline" data-action="pset-panel-close" title="סגור">✕</button>
    </div>
  </div>`;

  // Form area
  if (_setsFormMode === 'save-as') {
    html += `<div class="preset-inline-form" id="pset-saveas-form">
      <div class="preset-form-row">
        <label>שם: <input type="text" class="preset-name-input" data-field="pset-saveas-name" maxlength="60" placeholder="סט המשתתפים שלי" autofocus /></label>
        <label>תיאור: <input type="text" class="preset-desc-input" data-field="pset-saveas-desc" maxlength="200" placeholder="תיאור אופציונלי" /></label>
        <button class="btn-sm btn-primary" data-action="pset-saveas-confirm">שמור</button>
        <button class="btn-sm btn-outline" data-action="pset-form-cancel">ביטול</button>
      </div>
      <div class="preset-validation-error" id="pset-form-error">${_setsFormError}</div>
    </div>`;
  } else if (_setsFormMode === 'rename') {
    const targetId = _setsRenameTargetId ?? activeId;
    const target = targetId ? store.getParticipantSetById(targetId) : undefined;
    html += `<div class="preset-inline-form" id="pset-rename-form">
      <div class="preset-form-row">
        <label>שם: <input type="text" class="preset-name-input" data-field="pset-rename-name" maxlength="60" value="${escHtml(target?.name ?? '')}" /></label>
        <label>תיאור: <input type="text" class="preset-desc-input" data-field="pset-rename-desc" maxlength="200" value="${escHtml(target?.description ?? '')}" /></label>
        <button class="btn-sm btn-primary" data-action="pset-rename-confirm">שמור</button>
        <button class="btn-sm btn-outline" data-action="pset-form-cancel">ביטול</button>
      </div>
      <div class="preset-validation-error" id="pset-form-error">${_setsFormError}</div>
    </div>`;
  } else {
    html += `<div class="preset-actions-primary">
      <button class="btn-sm btn-primary" data-action="pset-new">+ שמור סט חדש</button>
    </div>`;
  }

  // Set list
  if (sets.length === 0) {
    html += `<div class="preset-empty"><span class="text-muted">אין סטים שמורים.</span></div>`;
  } else {
    html += `<div class="preset-list">`;
    for (const s of sets) {
      const isActive = s.id === activeId;
      const isBuiltIn = s.builtIn ?? false;
      const count = s.participants.length;
      html += `<div class="preset-item ${isActive ? 'preset-item-active' : ''}" data-pset-id="${s.id}">
        <div class="preset-item-main">
          <span class="preset-item-name">${escHtml(s.name)}</span>
          <span class="pset-count-badge">${count} משתתפים</span>
          ${isBuiltIn ? '<span class="preset-builtin-badge">מובנה</span>' : ''}
          ${isActive && dirty ? '<span class="preset-dirty-badge">שונה</span>' : ''}
          ${!isBuiltIn && s.createdAt ? `<span class="library-item-date text-muted">נשמר ${escHtml(formatMetadataDate(s.createdAt))}</span>` : ''}
        </div>
        ${s.description ? `<div class="preset-item-desc text-muted">${escHtml(s.description)}</div>` : ''}
        <div class="preset-item-actions">
          ${!isActive ? `<button class="btn-xs btn-primary" data-pset-action="load" data-pset-id="${s.id}" title="טען סט זה">▶ טען</button>` : ''}
          ${isActive && dirty && !isBuiltIn ? `<button class="btn-xs btn-outline" data-pset-action="update" data-pset-id="${s.id}" title="עדכן עם המשתתפים הנוכחיים">עדכן</button>` : ''}
          ${!isBuiltIn ? `<button class="btn-xs btn-outline" data-pset-action="rename" data-pset-id="${s.id}" title="שנה שם">✎</button>` : ''}
          <button class="btn-xs btn-outline" data-pset-action="duplicate" data-pset-id="${s.id}" title="שכפל">⧉</button>
          <button class="btn-xs btn-outline" data-pset-action="export" data-pset-id="${s.id}" title="ייצוא (JSON / Excel)">📤</button>
          ${!isBuiltIn ? `<button class="btn-xs btn-danger-outline" data-pset-action="delete" data-pset-id="${s.id}" title="מחק">${SVG_ICONS.trash}</button>` : ''}
        </div>
      </div>`;
    }
    html += `</div>`;
  }

  html += `</div>`;
  return html;
}

// ─── Footer Quote ─────────────────────────────────────────────────────────────

/** A compact editorial pull-quote at the very bottom of the participants list.
 *  Thematic: this screen is about people, and so is the quote. A slim accent
 *  bar on the inline-start (right, in RTL) edge marks it as a quote — small and
 *  distinctive, never competing with the functional table above. Static — no
 *  entrance animation — because the tab does a full structural rerender on every
 *  sort/filter/add and an animation would replay and flicker. The text is a
 *  hardcoded, developer-authored literal (not user input), so it is
 *  intentionally not escaped. */
function renderParticipantsQuote(): string {
  return `<figure class="participants-quote" role="note" aria-label="ציטוט">
    <blockquote class="participants-quote-text">תאהב אנשים, אתה כל היום מוקף אנשים, אל תשנא, אל תשנא בני־אדם גם כאלה שלא כל־כך מוצאים חן בעינייך.</blockquote>
    <figcaption class="participants-quote-author">שמעון פרס</figcaption>
  </figure>`;
}

// ─── Render ──────────────────────────────────────────────────────────────────

export function renderParticipantsTab(preflight?: PreflightResult): string {
  // ── Table Edit Mode branch ──
  if (isTableEditActive()) {
    return renderTableEditMode();
  }

  const allParticipants = store.getAllParticipants();
  const groups = store.getGroups();
  const sorted = getVisibleParticipants(allParticipants);
  reconcileSelection(sorted);
  reconcileExpanded();
  const visibleSelectedCount = sorted.reduce(
    (count, participant) => count + (selectedIds.has(participant.id) ? 1 : 0),
    0,
  );
  // Search is a DOM overlay (applySearchFilter), so the "select all visible"
  // controls operate on the group-filtered ∩ name-matching set.
  const searchVisible = sorted.filter(matchesSearch);
  const allSearchVisibleSelected = searchVisible.length > 0 && searchVisible.every((p) => selectedIds.has(p.id));
  const allRowsExpanded = sorted.length > 0 && sorted.every((p) => expandedIds.has(p.id));

  // Roster-level preflight Warnings render HERE — this tab is where the user
  // fixes the name, and no other surface shows an unanchored (no template/
  // one-time/slot id) Warning: the schedule/home/readiness panels are
  // Criticals-only and the task-rules badges key on anchor ids. Currently
  // scoped to DUPLICATE_PARTICIPANT_NAME; widen deliberately, not by default.
  const dupNameWarnings = (preflight?.findings ?? []).filter((f) => f.code === 'DUPLICATE_PARTICIPANT_NAME');

  let html = '';
  if (dupNameWarnings.length > 0) {
    html += `<div class="alert alert-warn" data-testid="dup-name-warning">
      <strong>⚠ שמות כפולים ברשימת המשתתפים</strong>
      <ul>${dupNameWarnings.map((f) => `<li>${escHtml(f.message)}</li>`).join('')}</ul>
    </div>`;
  }

  html += `
  <div class="tab-toolbar">
    <div class="toolbar-left">
      <h2>משתתפים <span class="count">${allParticipants.length}</span></h2>
      <div class="filter-pills">
        <button class="pill ${filterGroup === '' ? 'pill-active' : ''}" data-action="filter-group" data-group="">הכל</button>
        ${groups
          .map(
            (g) =>
              `<button class="pill ${filterGroup === g ? 'pill-active' : ''}" data-action="filter-group" data-group="${escHtml(g)}">${escHtml(g)}</button>`,
          )
          .join('')}
      </div>
      <div class="participants-controls">
        <input type="search" class="participant-search" data-action="participant-search" value="${escAttr(filterText)}" placeholder="חיפוש לפי שם…" maxlength="30" aria-label="חיפוש משתתף לפי שם" />
        <div class="participants-mobile-controls">
          <label class="sort-control">
            <span class="sort-control-label">מיון</span>
            <select class="input-sm" data-action="sort-select" aria-label="מיון לפי">
              <option value="" ${sortColumn === '' ? 'selected' : ''}>ברירת מחדל</option>
              <option value="name" ${sortColumn === 'name' ? 'selected' : ''}>שם</option>
              <option value="group" ${sortColumn === 'group' ? 'selected' : ''}>קבוצה</option>
              <option value="level" ${sortColumn === 'level' ? 'selected' : ''}>דרגה</option>
            </select>
          </label>
          <button class="btn-sm btn-outline sort-dir-btn" data-action="sort-dir-toggle" title="כיוון מיון" aria-label="כיוון מיון">${sortDirection === 'asc' ? '▲' : '▼'}</button>
          <button class="btn-sm btn-outline mobile-select-all-btn" data-action="mobile-select-all">${allSearchVisibleSelected ? 'נקה בחירה' : 'בחר הכל'}</button>
        </div>
      </div>
    </div>
    <div class="toolbar-right">
      <button class="btn-expand-all-mobile btn-sm btn-outline" data-action="toggle-all-details">${allRowsExpanded ? 'כווץ הכל' : 'הרחב הכל'}</button>
      <button class="btn-sm btn-outline${_setsPanelOpen ? ' pill-active' : ''}" data-action="pset-panel-toggle" title="סטים של משתתפים">📋 סטים${store.isParticipantSetDirty() ? ' <span class="dirty-dot"></span>' : ''}</button>
      <button class="btn-sm btn-outline" data-action="enter-table-edit" title="עריכת טבלה מרובה">✏️ עריכת טבלה</button>
      <button class="btn-primary btn-sm" data-action="add-participant">+ הוסף משתתף</button>
    </div>
  </div>`;

  // Participant Sets panel
  if (_setsPanelOpen) {
    html += renderSetsPanel();
  }
  // Table
  html += `<div class="table-responsive"><table class="table table-participants">
    <thead><tr>
      <th class="col-select"><input type="checkbox" id="cb-select-all" title="בחר הכל" ${visibleSelectedCount > 0 && visibleSelectedCount === sorted.length ? 'checked' : ''} /></th>
      <th class="col-index">#</th>
      <th class="col-name sortable-th" data-action="sort-column" data-sort-col="name">שם${sortIndicator('name')}</th>
      <th class="col-group sortable-th" data-action="sort-column" data-sort-col="group">קבוצה${sortIndicator('group')}</th>
      <th class="col-level sortable-th" data-action="sort-column" data-sort-col="level">דרגה${sortIndicator('level')}</th>
      <th class="col-certs">הסמכות</th>
      <th class="col-pakals">פק"לים</th>
      <th class="col-prefs">העדפות</th>
      <th class="col-avail">זמינות</th><th class="col-actions">פעולות</th><th class="col-expand"></th>
    </tr></thead><tbody>`;

  sorted.forEach((p, i) => {
    const dateRules = store.getDateUnavailabilities(p.id);
    const totalRules = dateRules.length;
    const isSelected = selectedIds.has(p.id);
    const isExpanded = expandedIds.has(p.id);
    const allPakalDefs = store.getAllPakalDefinitionsIncludeDeleted();

    html += `<tr data-participant-id="${p.id}" class="${isSelected ? 'row-selected' : ''}${isExpanded ? ' row-expanded' : ''}${totalRules > 0 ? ' has-unavail' : ''}">
        <td class="col-select"><input type="checkbox" class="cb-select-participant" data-pid="${p.id}" ${isSelected ? 'checked' : ''} /></td>
        <td class="col-index">${i + 1}</td>
        <td class="col-name" title="${escHtml(p.name)}">${hasOrphanedRefs(p) ? '<span class="badge-orphan-icon">⚠</span> ' : ''}<strong>${escHtml(p.name)}</strong>${renderUnavailChips(p.id)}</td>
        <td class="col-group">${groupBadge(p.group, true)}</td>
        <td class="col-level">${levelBadge(p.level)}${workloadMultBadge(p.workloadMultiplier)}</td>
        <td class="col-certs">${certBadges(p.certifications)}</td>
        <td class="col-pakals">${renderPakalBadges(p, allPakalDefs)}</td>
        <td class="col-prefs">${renderPreferenceBadges(p)}</td>
        <td class="col-avail avail-cell">
          <span class="mobile-label">זמינות: </span>${p.availability.map((w) => `<small dir="ltr">${fmtTime(w.start)}–${fmtTime(w.end)}</small>`).join('<br>')}
        </td>
        <td class="col-actions">
          <button class="btn-sm btn-outline btn-icon" data-action="edit-participant" data-pid="${p.id}" title="עריכה">${SVG_ICONS.edit}</button>
          <button class="btn-sm btn-outline btn-danger-outline btn-icon" data-action="remove-participant" data-pid="${p.id}" title="הסרה">${SVG_ICONS.trash}</button>
        </td>
        <td class="col-expand"><button class="btn-expand-details${totalRules > 0 ? ' has-unavail-badge' : ''}" data-action="toggle-details" data-pid="${p.id}" title="פרטים">${SVG_ICONS.chevronDown}${totalRules > 0 ? `<span class="expand-unavail-count" aria-label="${totalRules} כללי אי-זמינות">${totalRules}</span>` : ''}</button></td>
      </tr>`;
  });

  html += '</tbody></table></div>';

  // Empty state for an active filter that matches nobody. The search overlay
  // (applySearchFilter) toggles `hidden` live; render handles the group-filter case.
  const groupFilterEmpty = sorted.length === 0 && filterGroup !== '';
  html += `<div class="participant-search-empty"${groupFilterEmpty ? '' : ' hidden'}>לא נמצאו משתתפים</div>`;

  // ── Footer tribute quote — quiet closing note below all list content.
  //    Placed before the sticky bulk-toolbar so it's never occluded by it. ──
  html += renderParticipantsQuote();

  // ── Bulk Actions Toolbar ──
  // Always in the DOM (hidden when nothing is selected) so selection changes can
  // patch it in place via updateSelectionUI instead of forcing a full rerender.
  html += `<div class="bulk-toolbar"${selectedIds.size > 0 ? '' : ' hidden'}>
      <span class="bulk-count">${selectedIds.size} משתתפים נבחרו</span>
      <button class="btn-sm btn-outline" data-action="bulk-add-unavailability">${SVG_ICONS.calendar} הוסף אי-זמינות</button>
      <button class="btn-sm btn-danger-outline" data-action="bulk-delete-participants">${SVG_ICONS.trash} מחק משתתפים</button>
      <button class="btn-sm btn-outline" data-action="bulk-clear-selection">נקה בחירה</button>
    </div>`;

  // ── Bulk Unavailability Dialog ──
  if (_bulkDialogOpen) {
    html += renderBulkUnavailDialog();
  }

  // ── Bulk Delete Confirmation Dialog ──
  if (_bulkDeleteDialogOpen) {
    html += renderBulkDeleteDialog();
  }

  return html;
}

// ─── Bulk Unavailability Dialog ──────────────────────────────────────────────

function renderBulkUnavailDialog(): string {
  const nDays = store.getScheduleDays();
  const dayOptions = Array.from({ length: nDays }, (_, i) => `<option value="${i + 1}">יום ${i + 1}</option>`).join('');
  return `<div class="bulk-dialog-backdrop" data-action="bulk-dialog-dismiss">
    <div class="bulk-dialog">
      <h3>הוסף אי-זמינות עבור ${selectedIds.size} משתתפים</h3>

      <div class="bulk-dialog-body">
        <div class="form-row">
          <label>מיום
            <select class="input-sm" data-field="bulk-day-start">
              ${dayOptions}
            </select>
          </label>
          <label>עד יום
            <select class="input-sm" data-field="bulk-day-end">
              ${dayOptions}
            </select>
          </label>
        </div>

        <div class="form-row">
          <label class="checkbox-label">
            <input type="checkbox" data-field="bulk-allday" /> כל היום
          </label>
        </div>

        <div class="form-row bulk-time-fields">
          <label>שעת התחלה
            <input type="number" class="input-sm" data-field="bulk-start" min="0" max="23" value="8" style="width:70px" />
          </label>
          <span style="align-self:end;padding-bottom:4px">עד</span>
          <label>שעת סיום
            <input type="number" class="input-sm" data-field="bulk-end" min="0" max="23" value="16" style="width:70px" />
          </label>
        </div>

        <div class="form-row">
          <label style="flex:1">סיבה
            <input type="text" class="input-sm" data-field="bulk-reason" placeholder="למשל: הכשרת צוות" style="width:100%" />
          </label>
        </div>
      </div>

      <div class="bulk-dialog-footer">
        <button class="btn-sm btn-outline" data-action="bulk-dialog-cancel">ביטול</button>
        <button class="btn-primary btn-sm" data-action="bulk-dialog-save">שמור ל-${selectedIds.size} משתתפים</button>
      </div>
    </div>
  </div>`;
}

// ─── Bulk Delete Confirmation Dialog ─────────────────────────────────────────

function renderBulkDeleteDialog(): string {
  const n = selectedIds.size;
  return `<div class="bulk-dialog-backdrop" data-action="bulk-delete-dismiss">
    <div class="bulk-dialog bulk-delete-dialog">
      <h3>⚠️ למחוק ${n} משתתפים?</h3>
      <p class="bulk-delete-warning">
        האם למחוק <strong>${n}</strong> משתתפים?
        פעולה זו תסיר גם את כל השיבוצים וכללי אי-הזמינות המשויכים.
        לא ניתן לבטל פעולה זו.
      </p>
      <div class="bulk-dialog-footer">
        <button class="btn-sm btn-danger-outline" data-action="bulk-delete-confirm">אישור מחיקה</button>
        <button class="btn-sm btn-outline" data-action="bulk-delete-cancel">ביטול</button>
      </div>
    </div>
  </div>`;
}

// ─── Event Wiring ────────────────────────────────────────────────────────────

/** Apply the live name-search query as a DOM overlay: hide non-matching rows
 *  (without a rerender, so the search input keeps focus), toggle the empty state,
 *  and refresh the mobile select-all label. Safe to call on every render. */
function applySearchFilter(container: HTMLElement): void {
  const q = filterText.trim().toLowerCase();
  const rows = container.querySelectorAll<HTMLElement>('tr[data-participant-id]');
  let visibleCount = 0;
  rows.forEach((row) => {
    const pid = row.getAttribute('data-participant-id');
    const p = pid ? store.getParticipant(pid) : undefined;
    const match = q === '' || (!!p && p.name.toLowerCase().includes(q));
    row.classList.toggle('row-search-hidden', !match);
    if (match) visibleCount++;
  });
  const emptyEl = container.querySelector('.participant-search-empty');
  if (emptyEl) emptyEl.toggleAttribute('hidden', visibleCount > 0);
  updateSelectAllLabel(container);
}

/** Refresh the mobile "בחר הכל / נקה בחירה" toggle label from the current
 *  selection over the visible (group ∩ name-matching) set. */
function updateSelectAllLabel(container: HTMLElement): void {
  const selBtn = container.querySelector('[data-action="mobile-select-all"]');
  if (!selBtn) return;
  const visible = getVisibleParticipants().filter(matchesSearch);
  const allSel = visible.length > 0 && visible.every((p) => selectedIds.has(p.id));
  selBtn.textContent = allSel ? 'נקה בחירה' : 'בחר הכל';
}

/**
 * Patch every selection-dependent piece of UI in place — per-row highlight +
 * checkbox, the header select-all, the mobile select-all label, and the bulk
 * action bar — WITHOUT a full rerender, so expanded rows and the scroll
 * position are preserved and large rosters don't rebuild the whole app on each
 * tap. MUST stay in parity with the row builder + bulk-bar markup in
 * renderParticipantsTab (the single source of truth for the initial render).
 */
function updateSelectionUI(container: HTMLElement): void {
  // Per-row highlight + checkbox. Iterates every row (search-hidden included);
  // never touches `.row-search-hidden` (the search overlay is independent).
  container.querySelectorAll<HTMLElement>('tr[data-participant-id]').forEach((row) => {
    const pid = row.getAttribute('data-participant-id');
    if (!pid) return;
    const sel = selectedIds.has(pid);
    row.classList.toggle('row-selected', sel);
    const cb = row.querySelector<HTMLInputElement>('.cb-select-participant');
    if (cb) cb.checked = sel;
  });

  // Header select-all (desktop) — checked only when every group-visible row is
  // selected, mirroring the builder's `visibleSelectedCount === sorted.length`.
  const sorted = getVisibleParticipants();
  const visibleSelectedCount = sorted.reduce((n, p) => n + (selectedIds.has(p.id) ? 1 : 0), 0);
  const selectAllCb = container.querySelector<HTMLInputElement>('#cb-select-all');
  if (selectAllCb) {
    selectAllCb.checked = visibleSelectedCount > 0 && visibleSelectedCount === sorted.length;
    selectAllCb.indeterminate = visibleSelectedCount > 0 && visibleSelectedCount < sorted.length;
  }

  updateSelectAllLabel(container);

  // Bulk action bar — show/hide + count.
  const bulkBar = container.querySelector('.bulk-toolbar');
  if (bulkBar) {
    bulkBar.toggleAttribute('hidden', selectedIds.size === 0);
    const countEl = bulkBar.querySelector('.bulk-count');
    if (countEl) countEl.textContent = `${selectedIds.size} משתתפים נבחרו`;
  }

  syncBulkSelectionMode();
}

/** Mobile "selection mode": while participants are selected, the bottom tab-nav
 *  is swapped out for the bulk action bar (docked flush at the bottom) via a body
 *  class the mobile stylesheet keys off. Clearing the selection restores the nav.
 *  No-op visual on desktop (the class only drives mobile rules). Table-edit mode
 *  has its own toolbar, so it never enters selection mode. */
function syncBulkSelectionMode(): void {
  const active = !isTableEditActive() && selectedIds.size > 0;
  document.body.classList.toggle('bulk-select-mode', active);
}

/** Run a structural rerender while preserving the window scroll position, so an
 *  edit/removal mid-list doesn't jump the page to the top. */
function rerenderKeepingScroll(rerender: () => void): void {
  const y = window.scrollY;
  rerender();
  requestAnimationFrame(() => window.scrollTo(0, y));
}

export function wireParticipantsEvents(container: HTMLElement, rerender: () => void): void {
  // Keep selection mode (nav-swap) in sync after every (re)render.
  syncBulkSelectionMode();

  // ── Table Edit Mode branch ──
  if (isTableEditActive()) {
    wireTableEditEvents(container, rerender);
    return;
  }

  // ── Enter Table Edit button ──
  container.querySelector('[data-action="enter-table-edit"]')?.addEventListener('click', () => {
    enterTableEditMode();
    rerender();
  });

  // ─── Bulk: Select-All checkbox ─────────────────────────────────────────────
  const selectAllCb = container.querySelector('#cb-select-all') as HTMLInputElement | null;
  if (selectAllCb) {
    selectAllCb.addEventListener('change', () => {
      // Operate on the group-filtered ∩ name-matching set, not raw DOM checkboxes,
      // so search-hidden rows are never silently swept into the selection.
      if (selectAllCb.checked) {
        for (const p of getVisibleParticipants()) if (matchesSearch(p)) selectedIds.add(p.id);
      } else {
        selectedIds.clear();
      }
      _lastClickedId = null;
      updateSelectionUI(container);
    });
  }

  // ─── Name search (DOM overlay — no rerender, preserves input focus) ───
  const searchInput = container.querySelector('[data-action="participant-search"]') as HTMLInputElement | null;
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      filterText = searchInput.value;
      applySearchFilter(container);
    });
  }
  // Re-apply the overlay after every (re)render so the hidden class survives a rebuild.
  applySearchFilter(container);

  // ─── Bulk: Individual checkboxes (Shift+Click range, Ctrl+Click toggle) ───

  // ─── Group badge click → select entire group ──────────────────────────────
  container.querySelectorAll<HTMLElement>('[data-select-group]').forEach((badge) => {
    badge.addEventListener('click', (e) => {
      e.stopPropagation();
      const group = badge.dataset.selectGroup!;
      const all = store.getAllParticipants();
      const groupIds = all.filter((p) => p.group === group).map((p) => p.id);
      // If all group members are already selected, deselect them; otherwise select all
      const allSelected = groupIds.every((id) => selectedIds.has(id));
      if (allSelected) {
        for (const id of groupIds) selectedIds.delete(id);
      } else {
        for (const id of groupIds) selectedIds.add(id);
      }
      updateSelectionUI(container);
    });
  });

  container.querySelectorAll<HTMLInputElement>('.cb-select-participant').forEach((cb) => {
    cb.addEventListener('click', (e) => {
      e.stopPropagation();
      const pid = cb.dataset.pid!;
      const visiblePids = Array.from(container.querySelectorAll<HTMLInputElement>('.cb-select-participant')).map(
        (el) => el.dataset.pid!,
      );

      if (e.shiftKey && _lastClickedId) {
        // range-select between _lastClickedId and pid
        const from = visiblePids.indexOf(_lastClickedId);
        const to = visiblePids.indexOf(pid);
        if (from !== -1 && to !== -1) {
          const [lo, hi] = from < to ? [from, to] : [to, from];
          for (let i = lo; i <= hi; i++) selectedIds.add(visiblePids[i]);
        }
      } else if (e.ctrlKey || e.metaKey) {
        if (selectedIds.has(pid)) selectedIds.delete(pid);
        else selectedIds.add(pid);
      } else {
        // plain click — toggles the one checkbox
        if (selectedIds.has(pid)) selectedIds.delete(pid);
        else selectedIds.add(pid);
      }
      _lastClickedId = pid;
      updateSelectionUI(container);
    });
  });

  // ─── Bulk: Dialog live toggles (allDay) ─────────────────────────────────────
  container.addEventListener('change', (e) => {
    const el = e.target as HTMLElement;
    const field = el.getAttribute('data-field');
    if (field === 'bulk-allday') {
      const checked = (el as HTMLInputElement).checked;
      const dialog = el.closest('.bulk-dialog')!;
      const timeFields = dialog.querySelector('.bulk-time-fields') as HTMLElement;
      if (timeFields) timeFields.classList.toggle('hidden', checked);
    }
    // Mobile sort control (<select> fires change, not click)
    if (el.getAttribute('data-action') === 'sort-select') {
      sortColumn = (el as HTMLSelectElement).value as typeof sortColumn;
      rerender();
    }
  });

  container.addEventListener('click', async (e) => {
    const target = e.target as HTMLElement;

    // ── Participant Set item actions (load/update/rename/duplicate/delete) ──
    const psetButton = target.closest<HTMLElement>('[data-pset-action]');
    const psetAction = psetButton?.dataset.psetAction;
    if (psetAction) {
      const psetId = psetButton?.dataset.psetId;
      if (!psetId) return;
      await _handlePsetItemAction(psetAction, psetId, rerender);
      return;
    }

    const actionButton = target.closest<HTMLElement>('[data-action]');
    const action = actionButton?.dataset.action;
    if (!action) return;

    switch (action) {
      case 'sort-column': {
        const col = actionButton?.dataset.sortCol as typeof sortColumn;
        if (col === sortColumn) {
          sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
          sortColumn = col;
          sortDirection = 'asc';
        }
        rerender();
        break;
      }
      case 'filter-group': {
        filterGroup = actionButton?.dataset.group || '';
        rerender();
        break;
      }
      case 'sort-dir-toggle': {
        sortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
        rerender();
        break;
      }
      case 'mobile-select-all': {
        // Toggle selection over the visible (group ∩ name-matching) set.
        const visible = getVisibleParticipants().filter(matchesSearch);
        const allSelected = visible.length > 0 && visible.every((p) => selectedIds.has(p.id));
        for (const p of visible) {
          if (allSelected) selectedIds.delete(p.id);
          else selectedIds.add(p.id);
        }
        _lastClickedId = null;
        updateSelectionUI(container);
        break;
      }
      case 'add-participant': {
        const result = await showParticipantEditor({ mode: 'create' });
        if (result.saved) {
          rerender();
          // After rerender() the original `container` is detached — query the
          // live document for the fresh row. When the row is in the viewport,
          // anchor the burst there. Otherwise jitter a random point inside the
          // central area of the viewport — never a corner — so successive
          // additions don't bunch at one fixed pixel.
          if (result.participantId) {
            const newP = store.getParticipant(result.participantId);
            if (newP) {
              const newRow = document.querySelector(`tr[data-participant-id="${newP.id}"]`);
              const cx = window.innerWidth / 2;
              const cy = window.innerHeight / 2;
              const jitterX = (Math.random() - 0.5) * window.innerWidth * 0.4;
              const jitterY = (Math.random() - 0.5) * window.innerHeight * 0.25;
              let anchor = { x: cx + jitterX, y: cy + jitterY };
              if (newRow) {
                const r = newRow.getBoundingClientRect();
                const inViewport =
                  r.width > 0 &&
                  r.height > 0 &&
                  r.top >= 0 &&
                  r.left >= 0 &&
                  r.bottom <= window.innerHeight &&
                  r.right <= window.innerWidth;
                if (inViewport) {
                  anchor = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
                }
              }
              triggerCharacterEffect(newP.name, anchor, newRow);
            }
          }
        }
        break;
      }
      case 'edit-participant': {
        const pid = actionButton?.dataset.pid;
        if (!pid) break;
        const result = await showParticipantEditor({ mode: 'edit', participantId: pid });
        // Re-render whether saved or not — unavailability rules may have changed
        // even when the main draft was discarded (rules are committed live).
        // Keep scroll so editing someone mid-list doesn't jump back to the top.
        rerenderKeepingScroll(rerender);
        void result;
        break;
      }
      case 'remove-participant': {
        const pid = actionButton?.dataset.pid!;
        const p = store.getParticipant(pid);
        if (p) {
          const okRm = await showConfirm(`להסיר את ${p.name}?`, {
            danger: true,
            title: 'הסרת משתתף',
            confirmLabel: 'הסר',
          });
          if (okRm) {
            // Capture row anchor before rerender — the row is about to vanish.
            let farewellAnchor = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
            const row = container.querySelector(`tr[data-participant-id="${pid}"]`);
            if (row) {
              const r = row.getBoundingClientRect();
              farewellAnchor = { x: r.left + r.width / 2, y: r.top + r.height / 2 };
            }

            store.removeParticipant(pid);
            showToast(`${p.name} הוסר/ה בהצלחה`, {
              type: 'success',
              duration: 6000,
              action: {
                label: 'בטל',
                callback: () => {
                  store.undo();
                  rerender();
                },
              },
            });
            rerenderKeepingScroll(rerender);
            triggerCharacterFarewell(p.name, farewellAnchor);
          }
        }
        break;
      }
      case 'toggle-details': {
        const row = actionButton?.closest('tr');
        if (!row) break;
        const pid = row.getAttribute('data-participant-id');
        const nowExpanded = row.classList.toggle('row-expanded');
        // Mirror into module state so the expansion survives any later rerender.
        if (pid) {
          if (nowExpanded) expandedIds.add(pid);
          else expandedIds.delete(pid);
        }
        break;
      }
      case 'toggle-all-details': {
        const table = container.querySelector('.table-participants tbody');
        if (!table) break;
        const rows = table.querySelectorAll('tr[data-participant-id]');
        const allExpanded = Array.from(rows).every((r) => r.classList.contains('row-expanded'));
        rows.forEach((r) => {
          r.classList.toggle('row-expanded', !allExpanded);
          const pid = r.getAttribute('data-participant-id');
          if (!pid) return;
          if (allExpanded) expandedIds.delete(pid);
          else expandedIds.add(pid);
        });
        if (actionButton) {
          actionButton.textContent = allExpanded ? 'הרחב הכל' : 'כווץ הכל';
        }
        break;
      }
      case 'toggle-blackouts': {
        const pid = actionButton?.closest('[data-pid]')?.getAttribute('data-pid') || actionButton?.dataset.pid;
        if (!pid) break;
        await showParticipantEditor({ mode: 'edit', participantId: pid, scrollTo: 'unavailability' });
        rerender();
        break;
      }

      // ─── Participant Sets panel actions ────────────────────────────────────
      case 'pset-panel-toggle': {
        _setsPanelOpen = !_setsPanelOpen;
        _setsFormMode = 'none';
        _setsFormError = '';
        _setsRenameTargetId = null;
        rerender();
        break;
      }
      case 'pset-panel-close': {
        _setsPanelOpen = false;
        _setsFormMode = 'none';
        _setsFormError = '';
        _setsRenameTargetId = null;
        rerender();
        break;
      }
      case 'pset-import-xlsx': {
        void openXlsxImportFlow(() => rerender());
        break;
      }
      case 'pset-new': {
        _setsFormMode = 'save-as';
        _setsFormError = '';
        rerender();
        break;
      }
      case 'pset-saveas-confirm': {
        const nameInput = container.querySelector<HTMLInputElement>('[data-field="pset-saveas-name"]');
        const descInput = container.querySelector<HTMLInputElement>('[data-field="pset-saveas-desc"]');
        const psetName = nameInput?.value.trim() ?? '';
        const psetDesc = descInput?.value.trim() ?? '';
        if (!psetName) {
          _setsFormError = 'השם לא יכול להיות ריק';
          rerender();
          return;
        }
        const result = store.saveCurrentAsParticipantSet(psetName, psetDesc);
        if (!result) {
          _setsFormError = 'סט עם שם זה כבר קיים';
          rerender();
          return;
        }
        _setsFormMode = 'none';
        _setsFormError = '';
        showToast(`סט "${psetName}" נשמר בהצלחה`, { type: 'success' });
        rerender();
        break;
      }
      case 'pset-rename-confirm': {
        const renameId = _setsRenameTargetId ?? store.getActiveParticipantSetId();
        if (!renameId) return;
        const rnInput = container.querySelector<HTMLInputElement>('[data-field="pset-rename-name"]');
        const rdInput = container.querySelector<HTMLInputElement>('[data-field="pset-rename-desc"]');
        const rnName = rnInput?.value.trim() ?? '';
        const rnDesc = rdInput?.value.trim() ?? '';
        if (!rnName) {
          _setsFormError = 'השם לא יכול להיות ריק';
          rerender();
          return;
        }
        const rnErr = store.renameParticipantSet(renameId, rnName, rnDesc);
        if (rnErr) {
          _setsFormError = rnErr;
          rerender();
          return;
        }
        _setsFormMode = 'none';
        _setsFormError = '';
        _setsRenameTargetId = null;
        showToast('הסט עודכן בהצלחה', { type: 'success' });
        rerender();
        break;
      }
      case 'pset-form-cancel': {
        _setsFormMode = 'none';
        _setsFormError = '';
        _setsRenameTargetId = null;
        rerender();
        break;
      }

      // ─── Bulk toolbar & dialog actions ─────────────────────────────────────
      case 'bulk-add-unavailability': {
        _bulkDialogOpen = true;
        rerender();
        break;
      }
      case 'bulk-clear-selection': {
        selectedIds.clear();
        _lastClickedId = null;
        updateSelectionUI(container);
        break;
      }
      case 'bulk-delete-participants': {
        _bulkDeleteDialogOpen = true;
        rerender();
        break;
      }
      case 'bulk-delete-dismiss': {
        if (target !== actionButton) break;
        _bulkDeleteDialogOpen = false;
        rerender();
        break;
      }
      case 'bulk-delete-cancel': {
        _bulkDeleteDialogOpen = false;
        rerender();
        break;
      }
      case 'bulk-delete-confirm': {
        const ids = Array.from(selectedIds);
        const deleted = store.removeParticipantsBulk(ids);
        _bulkDeleteDialogOpen = false;
        selectedIds.clear();
        _lastClickedId = null;

        rerender();
        showToast(`${deleted} משתתפים נמחקו בהצלחה.`, {
          type: 'success',
          duration: 6000,
          action: {
            label: 'בטל',
            callback: () => {
              store.undo();
              rerender();
            },
          },
        });
        break;
      }
      case 'bulk-dialog-dismiss': {
        if (target !== actionButton) break;
        _bulkDialogOpen = false;
        rerender();
        break;
      }
      case 'bulk-dialog-cancel': {
        _bulkDialogOpen = false;
        rerender();
        break;
      }
      case 'bulk-dialog-save': {
        const dialog = container.querySelector('.bulk-dialog')!;
        const allDay = (dialog.querySelector('[data-field="bulk-allday"]') as HTMLInputElement).checked;
        const startHour = parseInt(
          (dialog.querySelector('[data-field="bulk-start"]') as HTMLInputElement).value || '0',
        );
        const endHour = parseInt((dialog.querySelector('[data-field="bulk-end"]') as HTMLInputElement).value || '0');
        const reason = (dialog.querySelector('[data-field="bulk-reason"]') as HTMLInputElement).value || undefined;

        const dayStart = parseInt(
          (dialog.querySelector('[data-field="bulk-day-start"]') as HTMLSelectElement).value || '1',
        );
        const dayEndRaw = parseInt(
          (dialog.querySelector('[data-field="bulk-day-end"]') as HTMLSelectElement).value || String(dayStart),
        );
        if (dayEndRaw < dayStart) {
          showToast('יום סיום חייב להיות גדול או שווה ליום ההתחלה.', { type: 'error' });
          break;
        }
        // Mirror the single-rule editor's guard: a zero-width same-day range
        // (start hour === end hour, not all-day) wraps to a full 24h blackout in
        // the engine (`endMs <= startMs` → +24h in computeAvailability /
        // isBlockedByDateUnavailability). Reject it so the bulk path matches
        // single-rule validation. A reversed range (start > end) is intentionally
        // allowed — it is the supported overnight-wrap pattern.
        if (!allDay && dayStart === dayEndRaw && startHour === endHour) {
          showToast('שעת התחלה ושעת סיום לא יכולות להיות זהות באותו יום. סמן "כל היום" אם זו הכוונה.', {
            type: 'error',
          });
          break;
        }
        const endDayIndex = dayEndRaw > dayStart ? dayEndRaw : undefined;
        const rule: Omit<import('../models/types').DateUnavailability, 'id'> = {
          dayIndex: dayStart,
          ...(endDayIndex !== undefined ? { endDayIndex } : {}),
          allDay,
          startHour: allDay ? 0 : startHour,
          endHour: allDay ? 24 : endHour,
          reason,
        };

        const count = store.addDateUnavailabilityBulk(Array.from(selectedIds), rule);
        _bulkDialogOpen = false;
        selectedIds.clear();
        _lastClickedId = null;

        rerender();
        showToast(`אי-זמינות נוספה בהצלחה עבור ${count} משתתפים.`, { type: 'success' });
        break;
      }
    }
  });
}

// ─── Participant Set Item Actions ────────────────────────────────────────────

async function _handlePsetItemAction(action: string, id: string, rerender: () => void): Promise<void> {
  switch (action) {
    case 'load': {
      const ok = await showConfirm('טעינת הסט תחליף את רשימת המשתתפים הנוכחית. להמשיך?', {
        danger: true,
        title: 'טעינת סט משתתפים',
        confirmLabel: 'טען',
      });
      if (!ok) return;
      store.loadParticipantSet(id);
      showToast('הסט נטען בהצלחה', { type: 'success' });
      rerender();
      break;
    }
    case 'update': {
      const ok = await showConfirm('לעדכן את הסט לפי רשימת המשתתפים הנוכחית?', {
        title: 'עדכון סט',
        confirmLabel: 'עדכן',
      });
      if (!ok) return;
      store.updateParticipantSet(id);
      showToast('הסט עודכן בהצלחה', { type: 'success' });
      rerender();
      break;
    }
    case 'rename': {
      _setsRenameTargetId = id;
      _setsFormMode = 'rename';
      _setsFormError = '';
      rerender();
      break;
    }
    case 'duplicate': {
      store.duplicateParticipantSet(id);
      showToast('הסט שוכפל בהצלחה', { type: 'success' });
      rerender();
      break;
    }
    case 'export': {
      const pset = store.getParticipantSetById(id);
      if (!pset) return;
      openParticipantSetFormatSheet(id, pset.name);
      break;
    }
    case 'delete': {
      const pset = store.getParticipantSetById(id);
      if (!pset || pset.builtIn) return;
      const ok = await showConfirm(`למחוק את הסט "${pset.name}"? לא ניתן לבטל פעולה זו.`, {
        danger: true,
        title: 'מחיקת סט',
        confirmLabel: 'מחק',
      });
      if (!ok) return;
      store.deleteParticipantSet(id);
      _setsFormMode = 'none';
      _setsFormError = '';
      _setsRenameTargetId = null;
      showToast('הסט נמחק בהצלחה', {
        type: 'success',
        duration: 6000,
        action: {
          label: 'בטל',
          callback: () => {
            store.undo();
            rerender();
          },
        },
      });
      rerender();
      break;
    }
  }
}
